import api from './api';
import * as actionTypes from './actionTypes';
import { sendHttpRequest, sendHttpCommandAndWaitForEvent } from 'actions/actionCreators/httpActions';
import { showError } from 'actions/actionCreators/snackbarActions';
import * as eventTypes from './eventTypes';
import { fileTypeExtensions } from 'constants/fileTypes';
import dmsApi from 'features/document/edit/documents/api';

export function loadOfferPaymentPlan(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isLoaded, isComputed } = getState().currentOffer.paymentPlanData;
    if (isLoaded) {
      return;
    }
    if (isComputed) {
      dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_STARTED, payload: { documentId, siteId } });
      try {
        let paymentPlan = await dispatch(sendHttpRequest(api.getPaymentPlan(documentId, siteId)));
        dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_SUCCESS, payload: { paymentPlan } });
      } catch (error) {
        dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_FAILED, payload: { documentId, siteId, error } });
        dispatch(showError(error.message, error.correlationId));
      }
    }
  };
}

export function loadOfferPaymentPlanSummary(documentId, siteId) {
  return async (dispatch, getState) => {
    let { summaryLoaded } = getState().currentOffer.paymentPlanData;
    if (summaryLoaded) {
      return;
    }
    dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_SUMMARY_STARTED, payload: { documentId, siteId } });
    try {
      let paymentPlanSummary = await dispatch(sendHttpRequest(api.getPaymentPlanSummary(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_SUMMARY_SUCCESS, payload: { paymentPlanSummary } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_OFFER_PAYMENT_PLAN_SUMMARY_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadScheduleTypeCellParamTemplates(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isLoaded, loading } = getState().currentOffer.paymentPlanData.scheduleTypeCellParamTemplates;
    if (isLoaded || loading) {
      return;
    }
    dispatch({
      type: actionTypes.LOAD_SCHEDULE_TYPE_CELL_PARAM_TEMPLATES_STARTED,
      payload: { documentId, siteId }
    });
    try {
      let scheduleTypeCellParamTemplates = await dispatch(sendHttpRequest(api.getScheduleTypeCellParamTemplates(documentId, siteId)));
      dispatch({
        type: actionTypes.LOAD_SCHEDULE_TYPE_CELL_PARAM_TEMPLATES_SUCCESS,
        payload: { scheduleTypeCellParamTemplates }
      });
    } catch (error) {
      dispatch({
        type: actionTypes.LOAD_SCHEDULE_TYPE_CELL_PARAM_TEMPLATES_FAILED,
        payload: { documentId, siteId, error }
      });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadDocumentScheduleCellParams(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isLoaded, loading } = getState().currentOffer.paymentPlanData.scheduleCellParams;
    if (isLoaded || loading) {
      return;
    }
    dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_CELL_PARAMS_STARTED, payload: { documentId, siteId } });
    try {
      let scheduleCellParams = await dispatch(sendHttpRequest(api.getDocumentScheduleCellParams(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_CELL_PARAMS_SUCCESS, payload: { scheduleCellParams } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_CELL_PARAMS_FAILED, payload: { documentId, siteId, error } });
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function loadScheduleTypeParams(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isLoaded } = getState().currentOffer.paymentPlanData.scheduleTypeParams;
    if (isLoaded) {
      return;
    }
    try {
      const scheduleTypeParams = await dispatch(sendHttpRequest(api.getScheduleTypeParams(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_SCHEDULE_PARAMS_TYPE_SUCCESS, payload: { scheduleTypeParams } });
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function loadDocumentScheduleParams(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isLoaded, loading } = getState().currentOffer.paymentPlanData.documentScheduleParams;
    let shouldCallApi = !loading && !isLoaded;
    if (!shouldCallApi) {
      return;
    }
    dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_PARAMS_STARTED });
    try {
      const documentScheduleParams = await dispatch(sendHttpRequest(api.getDocumentScheduleParams(documentId, siteId)));
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_PARAMS_SUCCESS, payload: { documentScheduleParams } });
    } catch (error) {
      dispatch({ type: actionTypes.LOAD_DOCUMENT_SCHEDULE_PARAMS_FAILED });
      dispatch(showError(error.message, error.correlationId));
      throw error;
    }
  };
}

export function saveDocumentScheduleParams(data) {
  return async dispatch => {
    dispatch({ type: actionTypes.SAVE_DOCUMENT_SCHEDULE_PARAMS_STARTED, payload: data });
    let event = await dispatch(
      sendHttpCommandAndWaitForEvent(api.updateDocumentScheduleParams(data), 'updateDocumentScheduleParams', [
        eventTypes.OFFER_SCHEDULE_PARAMS_UPDATED
      ])
    );
    if (event.ok) {
      dispatch({
        type: actionTypes.SAVE_DOCUMENT_SCHEDULE_PARAMS_SUCCESS,
        payload: data
      });
    } else {
      dispatch({
        type: actionTypes.SAVE_DOCUMENT_SCHEDULE_PARAMS_FAILED,
        payload: { data, error: event }
      });
    }
  };
}

export function updateDocumentScheduleCellParams(documentId, siteId, scheduleCellParams, scheduleTypeCellParamTemplateId) {
  return async dispatch => {
    dispatch({
      type: actionTypes.SAVE_DOCUMENT_SCHEDULE_CELL_PARAMS_STARTED,
      payload: { documentId, siteId, scheduleCellParams }
    });
    dispatch(
      sendHttpCommandAndWaitForEvent(
        api.updateDocumentScheduleCellParams(documentId, siteId, scheduleCellParams, scheduleTypeCellParamTemplateId),
        'UpdateDocumentScheduleCellParamsCommand',
        [eventTypes.DOCUMENT_SCHEDULE_CELL_PARAM_UPDATED]
      )
    )
      .then(event => {
        if (event.ok) {
          dispatch({
            type: actionTypes.SAVE_DOCUMENT_SCHEDULE_CELL_PARAMS_SUCCESS,
            payload: { documentId, siteId, scheduleCellParams }
          });
        } else {
          dispatch({
            type: actionTypes.SAVE_DOCUMENT_SCHEDULE_CELL_PARAMS_FAILED,
            payload: { documentId, siteId, scheduleCellParams, error: event }
          });
        }
      })
      .catch(error => {
        dispatch({
          type: actionTypes.SAVE_DOCUMENT_SCHEDULE_CELL_PARAMS_FAILED,
          payload: { documentId, siteId, scheduleCellParams, error }
        });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function loadAdvanceSplit(documentId, siteId) {
  return async (dispatch, getState) => {
    let { isAdvanceSplitLoaded } = getState().currentOffer.advanceSplitData;
    if (isAdvanceSplitLoaded) {
      return;
    }

    if (!isAdvanceSplitLoaded) {
      dispatch({ type: actionTypes.LOAD_OFFER_ADVANCE_SPLIT_STARTED, payload: { documentId, siteId } });
      try {
        let advanceSplit = await dispatch(sendHttpRequest(api.getAdvanceSplit(documentId, siteId)));
        dispatch({ type: actionTypes.LOAD_OFFER_ADVANCE_SPLIT_SUCCESS, payload: { advanceSplit } });
      } catch (error) {
        dispatch({ type: actionTypes.LOAD_OFFER_ADVANCE_SPLIT_FAILED, payload: { documentId, siteId, error } });
        dispatch(showError(error.message, error.correlationId));
      }
    }
  };
}

export function saveAdvanceSplit(advanceSplit) {
  return async dispatch => {
    dispatch({ type: actionTypes.SAVE_OFFER_ADVANCE_SPLIT_STARTED, payload: {} });

    await dispatch(
      sendHttpCommandAndWaitForEvent(api.saveAdvanceSplit(advanceSplit), 'SaveAdvanceSplitCommand', [eventTypes.OFFER_ADVANCE_SPLIT_SAVED])
    )
      .then(event => {
        if (event.ok) {
          dispatch({ type: actionTypes.SAVE_OFFER_ADVANCE_SPLIT_SUCCESS, payload: advanceSplit });
        } else {
          dispatch({ type: actionTypes.SAVE_OFFER_ADVANCE_SPLIT_FAILED, payload: advanceSplit });
        }
      })
      .catch(error => {
        dispatch({ type: actionTypes.SAVE_OFFER_ADVANCE_SPLIT_FAILED, payload: advanceSplit });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function importPaymentPlan(documentId, siteId, file) {
  return async dispatch => {
    dispatch({ type: actionTypes.IMPORT_PAYMENT_PLAN_STARTED });

    await dispatch(
      sendHttpCommandAndWaitForEvent(api.importPaymentPlan(documentId, siteId, file), 'ImportPaymentPlan', [
        eventTypes.OFFER_PAYMENT_PLAN_COMPUTED
      ])
    )
      .then(event => {
        if (event.ok) {
          dispatch({ type: actionTypes.IMPORT_PAYMENT_PLAN_SUCCESS });
        } else {
          dispatch({ type: actionTypes.IMPORT_PAYMENT_PLAN_FAILED });
        }
      })
      .catch(error => {
        dispatch({ type: actionTypes.IMPORT_PAYMENT_PLAN_FAILED });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function exportPaymentPlan(documentId, siteId, fileName, type, isVatFinancingSimulation = false) {
  return async dispatch => {
    if (type === 'excel')
      window.location = api.exportPaymentPlanExcel(documentId, siteId, `${fileName}.${fileTypeExtensions[type]}`, isVatFinancingSimulation);
    else {
      try {
        const event = await dispatch(
          sendHttpCommandAndWaitForEvent(api.exportPaymentPlan(documentId, siteId, fileName, isVatFinancingSimulation), null, [
            eventTypes.OFFER_PAYMENT_PLAN_EXPORTED
          ])
        );
        if (event.ok) {
          const url = dmsApi.buildDownloadUrl(documentId, siteId, event.eventBody.fileId, `${fileName}.${fileTypeExtensions[type]}`);
          window.location = url;
        }
      } catch (error) {
        dispatch(showError(error.message, error.correlationId));
      }
    }
  };
}

export function generateInvoice(documentId, siteId) {
  return async dispatch => {
    dispatch({ type: actionTypes.GENERATE_INVOICE_STARTED });

    await dispatch(
      sendHttpCommandAndWaitForEvent(api.generateInvoice(documentId, siteId), 'GenerateInvoiceCommand', [
        eventTypes.OFFER_PAYMENT_PLAN_COMPUTED
      ])
    )
      .then(event => {
        if (event.ok) {
          dispatch({ type: actionTypes.GENERATE_INVOICE_SUCCESS });
        } else {
          dispatch({ type: actionTypes.GENERATE_INVOICE_FAILED });
        }
      })
      .catch(error => {
        dispatch({ type: actionTypes.GENERATE_INVOICE_FAILED });
        dispatch(showError(error.message, error.correlationId));
      });
  };
}

export function cancelInvoice(documentId, siteId) {
  return async dispatch => {
    try {
      await dispatch(sendHttpCommandAndWaitForEvent(api.cancelInvoice(documentId, siteId), null, [eventTypes.INVOICE_CANCELLED]));
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}

export function downloadInvoice(documentId, siteId) {
  return async dispatch => {
    try {
      const event = await dispatch(
        sendHttpCommandAndWaitForEvent(api.downloadInvoice(documentId, siteId), null, [eventTypes.INVOICE_DOWNLOADED])
      );
      if (event.ok) {
        const url = dmsApi.buildDownloadUrl(documentId, siteId, event.eventBody.fileId, event.eventBody.fileName);
        window.location = url;
      }
    } catch (error) {
      dispatch(showError(error.message, error.correlationId));
    }
  };
}
